import { LANGUAGE_NAMES, brandCopy, type BrandCopy } from '@wm/brand';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ar from './locales/ar.json';
import en from './locales/en.json';
import fr from './locales/fr.json';

dayjs.extend(relativeTime);

export const RTL_LOCALES = ['ar'];
export const SUPPORTED_LOCALES = ['fr', 'ar', 'en'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/** The language pickers: each language under its own name, as on the Keycloak pages. */
export const LANGUAGES: { value: AppLocale; label: string }[] = SUPPORTED_LOCALES.map((value) => ({
  value,
  label: LANGUAGE_NAMES[value],
}));

function readStoredLocale(): AppLocale {
  try {
    const v = localStorage.getItem('wm.locale');
    if (v && (SUPPORTED_LOCALES as readonly string[]).includes(v)) return v as AppLocale;
  } catch {
    /* storage unavailable */
  }
  return 'fr';
}

function applyDocumentLocale(locale: AppLocale): void {
  document.documentElement.lang = locale;
  document.documentElement.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
  dayjs.locale(locale === 'ar' ? 'ar' : locale === 'en' ? 'en' : 'fr');
}

/**
 * Adds the brand copy (name, tagline, sign-in hero) from @wm/brand, which the Keycloak login pages
 * and e-mails share, under the keys the screens use.
 */
function withBrand<T extends { app: object; signIn: object }>(messages: T, copy: BrandCopy) {
  const [p1, p2, p3, p4] = copy.points;
  return {
    ...messages,
    app: { ...messages.app, brand: copy.name, tagline: copy.tagline },
    signIn: {
      ...messages.signIn,
      headline: copy.headline,
      lead: copy.lead,
      p1,
      p2,
      p3,
      p4,
      footer: copy.footer,
    },
  };
}

const initial = readStoredLocale();

void i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: withBrand(fr, brandCopy.fr) },
    ar: { translation: withBrand(ar, brandCopy.ar) },
    en: { translation: withBrand(en, brandCopy.en) },
  },
  lng: initial,
  fallbackLng: 'fr',
  interpolation: { escapeValue: false },
});
applyDocumentLocale(initial);

/** Whether the language was chosen on this device (it then wins over the profile's). */
export function hasStoredLocale(): boolean {
  try {
    return localStorage.getItem('wm.locale') !== null;
  } catch {
    return false;
  }
}

export function setLocale(locale: AppLocale): void {
  try {
    localStorage.setItem('wm.locale', locale);
  } catch {
    /* storage unavailable */
  }
  void i18n.changeLanguage(locale);
  applyDocumentLocale(locale);
}

export default i18n;
